<!-- eslint-disable vue/multi-word-component-names -->
<template>
  <label v-if="label" class="block text-md font-medium text-gray-700 -mb-3">
    {{ label }}
  </label>
  <Combobox
    :model-value="modelValue"
    @update:model-value="onSelect"
    :disabled="disabled"
    :multiple="multiple"
    as="div"
    class="w-full relative borde"
    :class="{ 'opacity-60 pointer-events-none': disabled }"
  >
    <div class="relative">
      <div
        class="h-10 relative w-full cursor-default overflow-hidden rounded-md border text-left focus:outline-none focus-within:ring-1 transition-colors duration-200 flex flex-wrap items-center bg-white gap-1.5"
        :class="[
          errorMessages
            ? 'border-red-500 focus-within:ring-red-500 focus-within:border-red-500'
            : 'border-gray-300 focus-within:ring-main focus-within:border-main',
          multiple ? 'p-1.5 ' : densityClasses,
        ]"
      >
        <div v-if="$slots.prependInner" class="pl-2 flex items-center text-gray-500">
          <slot name="prependInner"></slot>
        </div>

        <div v-if="multiple && Array.isArray(modelValue)" class="flex flex-wrap gap-1">
          <span
            v-for="val in modelValue"
            :key="val"
            class="inline-flex items-center gap-1 rounded bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700"
          >
            {{ getLabelFromValue(val) }}
            <button
              type="button"
              class="text-gray-400 hover:text-gray-600"
              @click.stop="removeItem(val)"
            >
              <Icon icon="heroicons:x-mark-20-solid" class="h-3 w-3" />
            </button>
          </span>
        </div>

        <ComboboxInput
          class="flex-1 border-none py-1 pl-2 pr-10 text-sm leading-5 text-gray-900 focus:ring-0 outline-none bg-transparent placeholder-gray-400 min-w-[50px]"
          :displayValue="(item: any) => (multiple ? '' : getItemTitle(item))"
          @change="onQueryChange"
          :placeholder="modelValue?.length > 0 && multiple ? '' : placeholder"
          :readonly="readonly"
          :type="type"
          autocomplete="off"
        />

        <div class="absolute inset-y-0 right-0 flex items-center pr-2 gap-1">
          <button
            v-if="clearable && (hasValue || query)"
            type="button"
            class="text-gray-400 hover:text-gray-600 focus:outline-none transition-colors"
            @click.stop="handleClear"
          >
            <Icon icon="$close" class="h-5 w-5" />
          </button>

          <div v-if="$slots.appendInner" class="flex items-center text-gray-500">
            <slot name="appendInner"></slot>
          </div>
        </div>
      </div>

      <TransitionRoot
        leave="transition ease-in duration-100"
        leave-from="opacity-100"
        leave-to="opacity-0"
        @after-leave="query = ''"
      >
        <ComboboxOptions
          v-if="query !== ''"
          :class="[
            inlineOptions
              ? 'relative mt-1 w-full rounded-md bg-white py-1 text-base shadow ring-opacity-5 focus:outline-none sm:text-sm'
              : 'absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-opacity-5 focus:outline-none sm:text-sm',
          ]"
        >
          <div
            v-if="filteredItems.length === 0 && query !== ''"
            class="relative cursor-default select-none py-2 px-4 text-gray-700"
          >
            {{ texteAucuneDonnee }}
          </div>

          <ComboboxOption
            v-for="(item, index) in filteredItems"
            as="template"
            :key="index"
            :value="item"
            v-slot="{ selected, active }"
          >
            <li
              class="relative cursor-pointer select-none py-2 pl-4 pr-4 transition-colors"
              :class="{
                'bg-blue-50 text-main': active,
                'text-gray-900': !active,
                'bg-blue-50/50': selected && !active,
              }"
            >
              <slot name="item" :item="item" :index="index" :selected="selected" :active="active">
                <span
                  class="block truncate"
                  :class="{ 'font-medium': selected, 'font-normal': !selected }"
                >
                  {{ getItemTitle(item) }}
                </span>
              </slot>
            </li>
          </ComboboxOption>
        </ComboboxOptions>
      </TransitionRoot>
    </div>

    <div v-if="errorMessages" class="mt-1 text-xs text-red-600 pl-1">
      {{ Array.isArray(errorMessages) ? errorMessages[0] : errorMessages }}
    </div>
  </Combobox>
</template>

<script setup lang="ts">
/* eslint-disable @typescript-eslint/no-explicit-any */
import { ref, computed } from 'vue'
import {
  Combobox,
  ComboboxInput,
  ComboboxOptions,
  ComboboxOption,
  TransitionRoot,
} from '@headlessui/vue'
import { Icon } from '@iconify/vue'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

const props = withDefaults(
  defineProps<{
    modelValue: any
    items: any[]
    itemValue?: string | ((item: any) => any)
    itemTitle?: string | ((item: any) => string)
    placeholder?: string
    clearable?: boolean
    disabled?: boolean
    readonly?: boolean
    errorMessages?: string | string[]
    noDataText?: string
    type?: string
    label?: string
    density?: 'default' | 'comfortable' | 'compact'
    multiple?: boolean
    /** When true, options list is in document flow (no absolute overlay) so parent scroll is the only scrollbar. */
    inlineOptions?: boolean
  }>(),
  {
    items: () => [],
    placeholder: '',
    clearable: false,
    disabled: false,
    readonly: false,
    errorMessages: undefined,
    noDataText: undefined,
    type: 'text',
    density: 'default',
    multiple: false,
    inlineOptions: false,
    itemValue: (item: any) => item,
    itemTitle: (item: any) => item?.name || item?.title || String(item),
  },
)

const emit = defineEmits<{
  'update:modelValue': [value: any]
  'update:search': [search: string]
  'click:clear': []
}>()

const query = ref('')

// Calculé plutôt que posé en défaut de prop : les défauts sont évalués avant setup, où naît `t`.
const texteAucuneDonnee = computed(() => props.noDataText ?? t('common.noDataShort'))

const getItemValue = (item: any) => {
  if (typeof props.itemValue === 'function') return props.itemValue(item)
  return item[props.itemValue as string]
}

const getItemTitle = (item: any) => {
  if (!item) return ''
  if (typeof props.itemTitle === 'function') return props.itemTitle(item)
  return item[props.itemTitle as string]
}

const getLabelFromValue = (value: any) => {
  const item = props.items.find((i) => getItemValue(i) === value)
  return item ? getItemTitle(item) : value
}

const hasValue = computed(() => {
  if (props.multiple) return Array.isArray(props.modelValue) && props.modelValue.length > 0
  return !!props.modelValue
})

// Combining diacritical marks range (U+0300 - U+036F), built via code points to avoid embedding raw combining characters in source.
const DIACRITICS_REGEX = new RegExp(`[${String.fromCharCode(0x0300)}-${String.fromCharCode(0x036f)}]`, 'g')

const normalizeText = (value: string) => value.toLowerCase().normalize('NFD').replace(DIACRITICS_REGEX, '')

const filteredItems = computed(() => {
  const search = normalizeText(query.value)
  const items = props.items
  if (search === '') return items
  return items.filter((item) => normalizeText(String(getItemTitle(item))).includes(search))
})

const densityClasses = computed(() => {
  switch (props.density) {
    case 'compact':
      return 'h-8'
    default:
      return 'h-10'
  }
})

const onQueryChange = (event: Event) => {
  const val = (event.target as HTMLInputElement).value
  query.value = val
  emit('update:search', val)
}

const onSelect = (item: any) => {
  if (props.multiple) {
    const value = getItemValue(item)
    const currentValues = Array.isArray(props.modelValue) ? [...props.modelValue] : []
    const index = currentValues.indexOf(value)

    if (index > -1) {
      currentValues.splice(index, 1)
    } else {
      currentValues.push(value)
    }
    emit('update:modelValue', currentValues)
  } else {
    emit('update:modelValue', getItemValue(item))
  }
  query.value = ''
}

const removeItem = (value: any) => {
  if (Array.isArray(props.modelValue)) {
    emit(
      'update:modelValue',
      props.modelValue.filter((v: any) => v !== value),
    )
  }
}

const handleClear = () => {
  query.value = ''
  emit('update:modelValue', props.multiple ? [] : null)
  emit('update:search', '')
  emit('click:clear')
}
</script>
